import { createFileRoute } from "@tanstack/react-router";
import { DollarSign, Calculator, Receipt, FileText, Users, ShieldCheck } from "lucide-react";
import { SolutionLanding } from "@/components/landing/SolutionLanding";

const URL = "https://busditeads.com/construction-payroll-software";
const TITLE = "Construction Payroll Software for Contractors — Business Dite Ads";
const DESC =
  "Construction payroll software for contractors: hourly and daily pay, time-tracking integration, paystubs and cost-per-project. 14-day free trial.";

const faqs = [
  {
    q: "Does it support hourly and daily pay (per diem)?",
    a: "Yes. Each employee can be configured as hourly or daily. Daily pay multiplies the rate by the number of distinct worked days; hourly multiplies by actual hours.",
  },
  {
    q: "Does payroll pull from time tracking automatically?",
    a: "Yes. Approved timesheets feed every payroll run, so you don't re-key hours or days — and labor cost is split per project automatically.",
  },
  {
    q: "Can workers see their own paystubs?",
    a: "Yes. Every worker has a private paystub view with breakdown of hours/days, rate and gross pay.",
  },
  {
    q: "Is it built for construction specifically?",
    a: "Yes. It handles multi-job labor distribution, daily rates common in construction, and ties cost back to project profitability.",
  },
];

export const Route = createFileRoute("/construction-payroll-software")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      {
        rel: "alternate",
        hrefLang: "pt",
        href: "https://busditeads.com/software-de-folha-de-pagamento-para-construcao",
      },
      {
        rel: "alternate",
        hrefLang: "es",
        href: "https://busditeads.com/software-de-nomina-para-construccion",
      },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SolutionLanding
      eyebrow="Construction Payroll Software"
      h1="Payroll built for construction — hourly, daily, multi-project"
      subhead="Pay your crew accurately every pay period. Hours and days flow from time tracking, costs split per project, and workers get clear digital paystubs."
      primaryCtaLabel="Start 14-day free trial"
      secondaryCtaLabel="See pricing"
      benefits={[
        "Hourly & daily rates",
        "Time tracking included",
        "Per-project cost split",
        "Cancel anytime",
      ]}
      featuresHeading="Payroll that understands how construction actually pays"
      features={[
        {
          icon: DollarSign,
          title: "Hourly or daily pay",
          description:
            "Toggle each worker between /hour and /day. The system calculates gross from actual worked hours or distinct worked days.",
        },
        {
          icon: Calculator,
          title: "Auto cost-per-project",
          description:
            "Daily and hourly pay are split proportionally across the jobs the worker spent time on — for accurate profit per project.",
        },
        {
          icon: Receipt,
          title: "Digital paystubs",
          description:
            "Generate PDF paystubs with hours/days, rate, gross pay and the period — emailable and downloadable.",
        },
        {
          icon: FileText,
          title: "Approve & pay flow",
          description:
            "Review the run, confirm each item, generate paystubs and lock the period. Every change is logged.",
        },
        {
          icon: Users,
          title: "Employees & subs",
          description:
            "Pay W-2 style employees and 1099 subcontractors from the same system, with role-based permissions.",
        },
        {
          icon: ShieldCheck,
          title: "Audit-ready records",
          description:
            "Immutable payroll history, paystub PDFs and exports for accountants and tax season.",
        },
      ]}
      howItWorksHeading="From hours to paid in three steps"
      steps={[
        {
          title: "Set rates per worker",
          description:
            "Configure each worker as hourly or daily with their rate. Edit anytime — history is preserved.",
        },
        {
          title: "Pull approved timesheets",
          description:
            "Start a new payroll run — approved hours and worked days are pulled in automatically.",
        },
        {
          title: "Confirm and issue paystubs",
          description: "Review, confirm each item, and generate paystub PDFs ready to send.",
        },
      ]}
      socialProofHeading="Why contractors trust our payroll"
      socialProof={[
        "Run payroll in minutes, not a full day",
        "Stop overpaying or underpaying — every minute is tracked",
        "Know exact labor cost per project before invoicing",
        "Workers see their own paystubs anytime",
        "Supports diárias (daily rates) common in residential construction",
        "Available in English, Spanish and Portuguese",
      ]}
      faqHeading="Frequently asked questions"
      faqs={faqs}
      finalCtaHeading="Stop dreading payday."
      finalCtaSubhead="Try Business Dite Ads free for 14 days and run your next payroll in minutes."
    />
  );
}
